import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { message, Table } from 'antd'
import { TableRowSelection } from 'antd/es/table/interface'
import { FC, Key, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
    useGetRewindQueueEntriesQuery,
    useReorderRewindQueueEntriesMutation,
} from '../../../../../../../store/api/rewind-queue-entries-api'
import RewindQueueEntry from '../../../../../../../types/api/rewind-queue-entry'
import { DocumentId } from '../../../../../../../types/strapi/strapi'
import CustomRow from '../../../../../flexo/components/machines-queues/components/custom-row'
import { getRewindQueueQuery } from '../../../../constants/query-params'
import { getRewindRowClassName } from '../../../../helpers/get-rewind-row-class-name'
import RewindQueueTableWrapper from '../../../rewind-queue-table-wrapper'
import { columns } from '../../columns'
import RowSelectionColumnTitle from './components/row-selection-column-title'

const TableWrapperStyled = styled(RewindQueueTableWrapper)`
    flex: auto;
    height: 100%;
    width: 100%;
`

interface MachineQueueProps {
    machineDocId: DocumentId
}

const MachineQueue: FC<MachineQueueProps> = ({ machineDocId }) => {
    const queryParams = useMemo(() => getRewindQueueQuery(machineDocId), [machineDocId])
    const { data, isLoading } = useGetRewindQueueEntriesQuery(queryParams)
    const [reorder] = useReorderRewindQueueEntriesMutation()
    const [dataSource, setDataSource] = useState<RewindQueueEntry[]>([])
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
    const selectedEntries = dataSource.filter(entry => selectedRowKeys.includes(entry.id))

    const rowSelection: TableRowSelection<RewindQueueEntry> = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        columnTitle: (
            <RowSelectionColumnTitle
                selected={selectedEntries}
                onMoved={() => setSelectedRowKeys([])}
            />
        ),
    }

    useEffect(() => {
        setDataSource(data?.data || [])
    }, [data?.data])

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (active.id === over?.id) return

        setDataSource(prevState => {
            const activeIndex = prevState.findIndex(record => record.id === active?.id)
            const overIndex = prevState.findIndex(record => record.id === over?.id)

            if (activeIndex < 0 || overIndex < 0) return prevState

            const newOrder = arrayMove(prevState, activeIndex, overIndex)

            reorder({
                data: newOrder.map((item, index) => ({ documentId: item.documentId, sort: index })),
            })
                .unwrap()
                .catch(() => {
                    setDataSource(prevState)
                    message.error('Не удалось сохранить порядок очереди перемотки')
                })

            return newOrder
        })
    }

    return (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
            <SortableContext items={dataSource.map(item => item.id)} strategy={verticalListSortingStrategy}>
                <TableWrapperStyled tableHasNotPagination filtersSummaryHeight={0}>
                    <Table
                        dataSource={dataSource}
                        columns={columns}
                        pagination={false}
                        rowKey='id'
                        size='small'
                        loading={isLoading}
                        components={{ body: { row: CustomRow } }}
                        rowClassName={record => getRewindRowClassName(record.rewind_job)}
                        rowSelection={rowSelection}
                    />
                </TableWrapperStyled>
            </SortableContext>
        </DndContext>
    )
}

export default MachineQueue
